import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { ShieldCheck, X } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  whileInView: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  },
  viewport: {
    once: true,
    margin: '-100px'
  }
};
export function ClosingSection() {
  const faqs = [{
    q: '¿Realmente funciona tan rápido?',
    a: 'Sí, porque actúa sobre el sistema nervioso, no sobre las hormonas.'
  }, {
    q: '¿Es difícil de aprender?',
    a: 'No, son pasos físicos simples que puedes hacer mientras estás sentada en una oficina.'
  }, {
    q: '¿Tengo que comprar productos caros?',
    a: 'No, los elementos del kit de supervivencia cuestan menos de $15 en cualquier tienda.'
  }, {
    q: '¿Se darán cuenta los demás?',
    a: 'Ese es el punto: el protocolo está diseñado para ser 100% invisible.'
  }];
  return <>
      {/* Proof / Believability */}
      <section className="bg-brand-sage py-24 px-6" data-id="element-31">
        <motion.div initial="hidden" whileInView="whileInView" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-4xl mx-auto text-center" data-id="element-32">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-brand-teal mb-8" data-id="element-33">
            Lo que cambia en 7 días
          </h2>
          <blockquote className="font-serif italic text-xl md:text-2xl text-brand-charcoal leading-relaxed relative" data-id="element-34">
            <span className="absolute -top-10 -left-6 text-6xl text-brand-teal/20" data-id="element-35">
              "
            </span>
            Pasarás de vivir con el miedo constante a "cuándo vendrá el próximo"
            a tener la tranquilidad de saber que, si llega, tienes el control
            para detenerlo en menos de dos minutos. La ansiedad desaparece
            cuando tienes un plan de acción.
            <span className="absolute -bottom-10 -right-6 text-6xl text-brand-teal/20" data-id="element-36">
              "
            </span>
          </blockquote>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-cream py-24 px-6" data-id="element-37">
        <div className="max-w-3xl mx-auto" data-id="element-38">
          <motion.h2 initial="hidden" whileInView="whileInView" viewport={{
          once: true
        }} variants={fadeInUp} className="font-serif text-3xl md:text-4xl font-bold text-brand-charcoal mb-12 text-center" data-id="element-39">
            Preguntas Frecuentes
          </motion.h2>

          <div className="space-y-6" data-id="element-40">
            {faqs.map((faq, index) => <motion.div key={index} initial="hidden" whileInView="whileInView" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-white p-6 rounded-2xl shadow-sm border border-brand-sage/50" data-id="element-41">
                <h3 className="font-serif font-bold text-xl text-brand-charcoal mb-3" data-id="element-42">
                  {faq.q}
                </h3>
                <p className="font-sans text-brand-charcoal/80 leading-relaxed" data-id="element-43">
                  {faq.a}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Offer + CTA */}
      <section id="offer" className="bg-brand-teal text-brand-cream py-24 px-6 text-center" data-id="element-44">
        <motion.div initial="hidden" whileInView="whileInView" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-45">
          <h2 className="font-serif text-3xl md:text-5xl font-bold mb-8" data-id="element-46">
            Recupera tu confianza y el control de tu cuerpo hoy mismo.
          </h2>
          <p className="font-sans text-xl text-brand-cream/90 mb-12 leading-relaxed" data-id="element-47">
            No permitas que un síntoma biológico decida cómo te sientes en tu
            mejor etapa. Obtén el Protocolo de Emergencia ahora:
          </p>
          <div className="flex flex-col items-center gap-2 mb-2" data-id="element-48">
            <span className="inline-block bg-brand-coral text-white font-sans font-bold text-sm px-4 py-1 rounded-full tracking-wide" data-id="element-49">
              68% DE DESCUENTO
            </span>
            <div className="flex items-baseline gap-4" data-id="element-50">
              <span className="text-brand-cream/50 line-through text-2xl font-sans" data-id="element-51">
                $19.99 USD
              </span>
              <span className="text-white text-5xl md:text-6xl font-serif font-bold" data-id="element-52">
                $6.39 <span className="text-2xl" data-id="element-53">USD</span>
              </span>
            </div>
            <span className="text-brand-cream/70 font-sans text-sm" data-id="element-54">
              Pago único — sin suscripciones
            </span>
          </div>
          <div className="flex flex-col items-center gap-6 mt-10" data-id="element-55">
            <CTAButton className="w-full md:w-auto text-xl py-5" href="https://clavesdebienestar.com/cart/53284719362232:1" data-id="element-56" />
            <div className="flex items-center gap-2 text-brand-cream/80 font-sans text-sm" data-id="element-57">
              <ShieldCheck className="w-5 h-5 text-brand-coral" data-id="element-58" />
              <span data-id="element-59">
                Compra segura. Acceso instantáneo por correo electrónico.
              </span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <Footer data-id="element-60" />
    </>;
}
function Footer() {
  const [modal, setModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="bg-brand-charcoal py-8 px-6 font-sans text-sm" data-id="element-61">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-62">
          <p className="text-white/50" data-id="element-63">
            &copy; {new Date().getFullYear()} Menopausia sin Niebla. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-64">
            <button onClick={() => setModal('privacy')} className="text-white/50 hover:text-white transition-colors underline underline-offset-2" data-id="element-65">
              Privacidad
            </button>
            <button onClick={() => setModal('terms')} className="text-white/50 hover:text-white transition-colors underline underline-offset-2" data-id="element-66">
              Términos
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-67">
        {modal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModal(null)} data-id="element-68">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative" onClick={e => e.stopPropagation()} data-id="element-69">
              <div className="sticky top-0 bg-white rounded-t-2xl border-b border-brand-sage/30 px-8 py-5 flex items-center justify-between" data-id="element-70">
                <h2 className="font-serif text-2xl font-bold text-brand-charcoal" data-id="element-71">
                  {modal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={() => setModal(null)} className="text-brand-charcoal/50 hover:text-brand-charcoal transition-colors p-1" data-id="element-72">
                  <X className="w-6 h-6" data-id="element-73" />
                </button>
              </div>
              <div className="px-8 py-6 font-sans text-brand-charcoal/80 leading-relaxed space-y-4 text-sm" data-id="element-74">
                {modal === 'privacy' ? <PrivacyContent data-id="element-75" /> : <TermsContent data-id="element-76" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="font-semibold text-brand-charcoal text-base" data-id="element-77">
        Última actualización: Abril 2026
      </p>
      <p data-id="element-78">
        En Menopausia sin Niebla, respetamos tu privacidad y nos comprometemos a
        proteger tu información personal.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-79">
        Información que recopilamos
      </h3>
      <p data-id="element-80">
        Recopilamos únicamente la información necesaria para procesar tu compra
        y entregarte el producto digital: tu dirección de correo electrónico y
        los datos de pago procesados de forma segura por nuestro proveedor de
        pagos.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-81">
        Uso de la información
      </h3>
      <p data-id="element-82">
        Tu información se utiliza exclusivamente para: entregar el producto
        digital adquirido, enviar confirmaciones de compra y proporcionar
        soporte al cliente si lo solicitas.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-83">
        Protección de datos
      </h3>
      <p data-id="element-84">
        No vendemos, intercambiamos ni transferimos tu información personal a
        terceros. Implementamos medidas de seguridad estándar de la industria
        para proteger tus datos.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-85">
        Cookies
      </h3>
      <p data-id="element-86">
        Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
        navegación. Puedes configurar tu navegador para rechazar cookies si lo
        prefieres.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-87">
        Tus derechos
      </h3>
      <p data-id="element-88">
        Puedes solicitar la eliminación de tus datos personales en cualquier
        momento. Responderemos a tu solicitud en un plazo máximo de 30 días.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="font-semibold text-brand-charcoal text-base" data-id="element-89">
        Última actualización: Abril 2026
      </p>
      <p data-id="element-90">
        Al adquirir el Protocolo de Emergencia de Menopausia sin Niebla, aceptas
        los siguientes términos y condiciones.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-91">
        Producto digital
      </h3>
      <p data-id="element-92">
        Este es un producto digital informativo. Recibirás acceso instantáneo al
        material a través del correo electrónico proporcionado al momento de la
        compra.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-93">
        Uso personal
      </h3>
      <p data-id="element-94">
        El contenido adquirido es para tu uso personal únicamente. Queda
        prohibida la reproducción, distribución o reventa del material sin
        autorización expresa.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-95">
        Descargo de responsabilidad
      </h3>
      <p data-id="element-96">
        Este producto es una herramienta informativa y educativa. No sustituye
        el consejo médico profesional. Si tienes condiciones de salud
        específicas, consulta con tu médico antes de aplicar cualquier técnica.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-97">
        Política de reembolso
      </h3>
      <p data-id="element-98">
        Debido a la naturaleza digital del producto y el acceso instantáneo,
        todas las ventas son finales. Si experimentas problemas técnicos para
        acceder al material, nos comprometemos a resolverlos.
      </p>
      <h3 className="font-serif font-bold text-lg text-brand-charcoal pt-2" data-id="element-99">
        Modificaciones
      </h3>
      <p data-id="element-100">
        Nos reservamos el derecho de actualizar estos términos. Cualquier cambio
        será efectivo inmediatamente después de su publicación en esta página.
      </p>
    </>;
}